import type { TCards } from './cc-images';
import type { IPaymentMethods } from '../models/order-details';
import type { ITenders } from '../models/common';

/**
 * Tender resolution, shared by the payment icons and the analytics/telemetry
 * payloads so they never disagree about what a customer paid with.
 *
 * Argument order is `(paymentMethods, tenders)` in every helper, the same order
 * Self-Service Returns declares -- keep the two in step, since a silent swap
 * between the apps would be invisible at the call site. That is a calling
 * convention, not the precedence: `resolvePrimaryTenderCode` prefers the
 * payment method, while `resolveTenderCodes` enumerates `tenders` whenever the
 * order has any. Each helper states which it uses and why.
 *
 * Every import here is type-only on purpose: `payment-context.ts` is kept free
 * of React, and `cc-images` pulls it in.
 */

/** Terminal entry modes that identify a mobile wallet rather than a card swipe. */
const APPLE_PAY_ENTRY_MODE = 'APPLE_PAY';
const GOOGLE_PAY_ENTRY_MODE = 'GOOGLE_PAY';

/** Either shape a payment reaches the UI as. */
export type PaymentLike = IPaymentMethods | ITenders;

const distinct = (codes: (TCards | undefined)[]): TCards[] =>
  Array.from(
    new Set(
      codes
        .filter((code): code is TCards => Boolean(code))
        .map((code) => code.toUpperCase() as TCards),
    ),
  );

/**
 * The wallet a payment was presented with, if any. Only `paymentMethods[]`
 * carries `entryMode`, which is why a wallet is invisible on a tender.
 */
const walletCode = (payment?: PaymentLike): TCards | undefined => {
  const entryMode = (payment as IPaymentMethods)?.entryMode;
  if (entryMode === APPLE_PAY_ENTRY_MODE) {
    return 'APPLE';
  }
  if (entryMode === GOOGLE_PAY_ENTRY_MODE) {
    return 'GOOGLE';
  }
  return undefined;
};

/** The card behind a payment, ignoring any wallet it was presented with. */
const cardCode = (payment?: PaymentLike): TCards | undefined =>
  ((payment as IPaymentMethods)?.cardType as TCards) ||
  ((payment as ITenders)?.type as TCards) ||
  undefined;

/**
 * Resolves the code to display and report for one payment.
 *
 * A wallet payment reports its *funding card* on both `cardType` and `type`
 * (VI, MA, AX), so neither can say "Apple Pay" on its own — the wallet is only
 * visible on `entryMode`. Everything else falls back to `cardType`, then to the
 * tender's `type`.
 */
export const resolveTenderCode = (payment?: PaymentLike): TCards | undefined =>
  walletCode(payment) || cardCode(payment);

/**
 * The single code to report where only one is wanted (the Adobe tag).
 *
 * The wallet is searched for across every payment method before anything else,
 * because it can sit at any index: on a gift-card + Apple Pay split tender the
 * wallet is `paymentMethods[1]`, and reading index 0 alone would report the
 * gift card and lose the wallet entirely.
 *
 * Failing that, `paymentMethods[0]` wins over the tender, but it is not trusted
 * to be complete: OVQ can send an entry whose code sits on `paymentType` rather
 * than `cardType`, or send `{}`, and the tender still describes that payment.
 */
export const resolvePrimaryTenderCode = (
  paymentMethods?: IPaymentMethods[],
  tenders?: ITenders[],
): TCards | undefined =>
  (paymentMethods ?? []).map(walletCode).find(Boolean) ||
  resolveTenderCode(paymentMethods?.[0]) ||
  resolveTenderCode(tenders?.[0]);

/**
 * The raw code of every payment on the order, deduped and upper-cased.
 *
 * Deliberately excludes the wallet: these are the codes the order actually
 * carries, so one Apple Pay payment stays one tender rather than becoming a
 * two-tender split. `tenders[]` is the enumeration source, as it was before
 * wallets were surfaced -- the two arrays are independent projections whose
 * lengths need not agree, so selecting one over the other drops co-tenders.
 * When there are no tenders at all, `paymentMethods[]` becomes the source.
 */
export const resolveTenderCodes = (
  paymentMethods?: IPaymentMethods[],
  tenders?: ITenders[],
): TCards[] =>
  distinct((tenders?.length ? tenders : paymentMethods ?? []).map(cardCode));

/** The wallet marks on the order, which only `paymentMethods[]` can name. */
export const resolveWalletCodes = (
  paymentMethods?: IPaymentMethods[],
): TCards[] => distinct((paymentMethods ?? []).map(walletCode));
